import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Heart, Phone, User, Menu, X, UserPlus, LogIn, Calendar, Stethoscope, Video, Shield } from 'lucide-react';
import useAppStore from '../../store/useAppStore';
import './Header.css';

const Header = () => {
  const location = useLocation();
  const { mobileMenuOpen, setMobileMenuOpen, isAuthenticated, user } = useAppStore();
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle emergency call
  const handleEmergencyCall = () => {
    if (window.confirm('This will call emergency services (108). Are you sure?')) {
      window.location.href = 'tel:108';
    }
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Close mobile menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.header-content')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen, setMobileMenuOpen]);

  const navItems = [
    { path: '/find-doctors', label: 'Find Doctors', icon: Stethoscope },
    { path: '/book-appointment', label: 'Book Appointment', icon: Calendar },
    { path: '/telemedicine', label: 'Telemedicine', icon: Video },
    { path: '/patient-portal', label: 'Patient Portal', icon: User },
    { path: '/emergency', label: 'Emergency', icon: Shield }
  ];

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="header-content">
          {/* Logo */}
          <Link to="/" className="logo">
            <Heart className="logo-icon" />
            <span>DoctDash</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="main-nav">
            <ul className="nav-links">
              {navItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <li key={item.path} className="nav-item">
                    <Link
                      to={item.path}
                      className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                    >
                      <IconComponent size={18} className="nav-icon" />
                      <span className="nav-text">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Header Actions */}
          <div className="header-actions">
            {/* Emergency Button */}
            <button
              className="emergency-btn"
              onClick={handleEmergencyCall}
              aria-label="Call Emergency Services"
            >
              <Phone size={16} />
              <span className="emergency-text">Emergency</span>
            </button>
            
            {/* Auth Buttons */}
            {isAuthenticated ? (
              <div className="user-menu">
                <Link to="/patient-portal" className="user-link">
                  <User size={18} />
                  <span>{user?.name || 'My Account'}</span>
                </Link>
              </div>
            ) : (
              <div className="auth-buttons">
                <Link to="/login" className="auth-btn auth-btn-secondary">
                  <LogIn size={16} />
                  <span>Sign In</span>
                </Link>
                <Link to="/register" className="auth-btn auth-btn-primary">
                  <UserPlus size={16} />
                  <span>Sign Up</span>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-btn"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className={`mobile-nav ${mobileMenuOpen ? 'active' : ''}`}>
          <div className="mobile-nav-content">
            {/* Mobile Auth Buttons - Only show if not authenticated */}
            {!isAuthenticated && (
              <div className="mobile-auth-section">
                <Link 
                  to="/login" 
                  className="mobile-auth-btn mobile-auth-btn-secondary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <LogIn size={18} />
                  <span>Sign In</span>
                </Link>
                <Link 
                  to="/register" 
                  className="mobile-auth-btn mobile-auth-btn-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <UserPlus size={18} />
                  <span>Sign Up</span>
                </Link>
              </div>
            )}
            
            {/* Mobile Navigation Links */}
            <ul className="mobile-nav-links">
              {navItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <li key={item.path} className="mobile-nav-item">
                    <Link
                      to={item.path}
                      className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <IconComponent size={20} className="mobile-nav-icon" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;